import React from "react";
import { Row, Card, Col } from "react-bootstrap";
import { withRouter } from "react-router-dom";
import { bindActionCreators } from "redux";
import { connect, useSelector } from "react-redux";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import ToggleButton from 'react-toggle-button';
import _ from "lodash";

import { getCurrentCognitoUser, changeUserAttribute } from "../../actions/auth";
import { getUserInfo } from "../../actions/settings";
import HistoryCard from "../../components/dashboard/HistoryCard";
import SettingsItem from "../../components/settings/SettingsItem";
import SettingsMultiSelector from "../../components/settings/SettingsMultiSelector";
import AvatarUploader from "../../components/settings/AvatarUploader";
import ChangePasswordForm from "../../components/settings/ChangePasswordForm";
import ChangeUserSettingForm from "../../components/settings/ChangeUserSettingForm";
import { USER_SETTINGS } from "../../constants/UserSettings";
import "./index.scss";

const ReactSwal = withReactContent(Swal);

const Settings = props => {
  React.useEffect(() => {
    props.getUserInfo();
  }, []);

  const getCtaUrl = () => {
    // If a custom ctaUrl is provided, use it; otherwise, default to a mailto link
    return _.get(props.userInfo, "ctaUrl", `${_.get(props.cognitoUser, "attributes.email")}`);
  };

  // const ctaUrl = useSelector
  const toTitleCase = (theString) => {
    return _.startCase(_.toLower(theString));
  }

  const onChangePassword = () => {
    ReactSwal.fire({
      allowOutsideClick: true,
      allowEscapeKey: true,
      allowEnterKey: false,
      showCancelButton: false,
      showConfirmButton: false,
      html: (
        <ChangePasswordForm
          onClose={ReactSwal.clickCancel}
          onConfirm={ReactSwal.clickConfirm}
          Auth={props.Auth}
          cognitoUser={props.cognitoUser}
        />
      )
    })
    .then(result => {
      if (result.value) {
        // additional logic if needed
      }
    });
  }

  /**
   * Open a modal to update a single user setting.
   *
   * @param {String} field - The attribute name to update.
   * @param {String} title - The title for the modal (e.g., "Change Name").
   * @param {String} label - The label for the input field (e.g., "Name").
   * @param {String} initialValue - The current value for that field.
   */
  const onUpdateUserSetting = (field, title, label, initialValue, usePlaceholder = true) => {
    ReactSwal.fire({
      allowOutsideClick: true,
      allowEscapeKey: true,
      allowEnterKey: false,
      showCancelButton: false,
      showConfirmButton: false,
      html: (
        <ChangeUserSettingForm
          title={title}
          label={label}
          initialValue={initialValue}
          usePlaceholder={usePlaceholder}
          onClose={ReactSwal.clickCancel}
          onConfirm={async (newValue) => {
            // Update the user attribute and then close the modal
            await props.changeUserAttribute({ name: field, value: newValue, authAttribute: false });
            ReactSwal.clickConfirm();
          }}
        />
      )
    }).then(result => {
      // Optionally handle result.value if needed.
    });
  };

  /**
   * Change the notification settings for the user.
   * Options are stored in the form:
   *  appOptions: { pushEnabled: true }
   *  extensionOptions: { pushEnabled: false }
   * 
   * @param {String} settingName 
   * @param {BOOL} value 
   */
  const onToggleNotificationSettings = async (settingName, value) => {
    let attributeName = "";

    switch (settingName) {
      case USER_SETTINGS.APP_OPTIONS.PUSH_ENABLED:
        attributeName = USER_SETTINGS.APP_OPTIONS.ATTRIBUTE;
        break;
      case USER_SETTINGS.EXTENSION_OPTIONS.PUSH_ENABLED:
        attributeName = USER_SETTINGS.EXTENSION_OPTIONS.ATTRIBUTE;
        break;
    
      default:
        break;
    }

    if (attributeName) {
      const currentOptions = _.get(props.userInfo, attributeName, { pushEnabled: true });
      currentOptions.pushEnabled = !value;
  
      props.changeUserAttribute({ name: attributeName, value: currentOptions, authAttribute: false });
    }
  }

  const onChangeDefaultFilter = (key, value) => {
    if (value) {
      props.changeUserAttribute({
        name: 'searchOptions',
        value: {
          ..._.get(props.userInfo, 'searchOptions', {}),
          [key]: value
        },
        authAttribute: false
      });
    } else {
      const newSettings = _.get(props.userInfo, 'searchOptions', {});
      delete newSettings[key];

      props.changeUserAttribute({
        name: 'searchOptions',
        value: newSettings,
        authAttribute: false
      });
    }
  }

  const { name, email } = props.cognitoUser.attributes;
  const role = _.get(props.cognitoUser, 'attributes["custom:role"]', 'user');

  return (
    <div className="settings-container">
      <div className="profile-sidebar">
        <div className="profile-edit">
          <h3>Profile</h3>
        </div>
        <AvatarUploader
          user={props.user}
          cognitoUser={props.cognitoUser}
          getCurrentCognitoUser={props.getCurrentCognitoUser}
          Auth={props.Auth}
        />
        <div className="role-box">
          <span className="name">{name}</span>
          <span className="role"><i>{toTitleCase(role)}</i></span>
        </div>
        <div className="personal-details">
          <div className="personal-info">
            <span className="key">COMPANY</span>
            <span className="value">{_.get(props.user, 'companyName', '')}</span>
          </div>
        </div>
      </div>

      <div className="settings-content">
        <h3 className="mb-4 mt-4">Settings</h3>
        <Row className="full-width">
          <div className="security-section flex items-start">
            <Card bg="light" className="full-width mr-4">
              <Card.Header>Details</Card.Header>
              <Card.Body>
                <SettingsItem
                  title="NAME"
                  value={name}
                  buttonTitle="CHANGE"
                  onClick={() => onUpdateUserSetting("name", "Change Name", "Name", name, true)}
                />
                <SettingsItem
                  title="CONTACT EMAIL / URL"
                  value={getCtaUrl()}
                  buttonTitle="CHANGE"
                  onClick={() => onUpdateUserSetting("ctaUrl", "Change Contact Email / Url", "Contact Email / Url", getCtaUrl(), false)}
                />
              </Card.Body>
            </Card>
            <Card bg="light" className="full-width mr-4">
              <Card.Header>Security</Card.Header>
              <Card.Body>
                <SettingsItem
                  title="USER EMAIL"
                  value={email}
                  buttonTitle=""
                />
                <SettingsItem
                  title="PASSWORD"
                  value="●●●●●●●●●●●"
                  buttonTitle="CHANGE"
                  onClick={onChangePassword}
                />
              </Card.Body>
            </Card>
          </div>
        </Row>

        {/* New row containing both Search Filters and Notification Settings */}
        <Row className="full-width">
          <Col md={6}>
            <Card bg="light">
              <Card.Header>Search Filters</Card.Header>
              <Card.Body>
                <SettingsMultiSelector
                  title="Tags"
                  options={_.get(props.settings, 'contentTags', [])}
                  selected={_.get(props.userInfo, 'searchOptions.tags', [])}
                  onChange={values => onChangeDefaultFilter('tags', values)}
                />
                {_.get(props.settings, 'appOptions.enableProducts', false) &&
                  <SettingsMultiSelector
                    title="Products"
                    options={_.get(props.settings, 'contentProducts', [])}
                    selected={_.get(props.userInfo, 'searchOptions.products', [])}
                    onChange={values => onChangeDefaultFilter('products', values)}
                  />
                }
                {_.get(props.settings, 'appOptions.enableIndustries', false) &&
                  <SettingsMultiSelector
                    title="Industries"
                    options={_.get(props.settings, 'contentIndustries', [])}
                    selected={_.get(props.userInfo, 'searchOptions.industries', [])}
                    onChange={values => onChangeDefaultFilter('industries', values)}
                  />
                }
                {_.get(props.settings, 'appOptions.enableRegions', false) &&
                  <SettingsMultiSelector
                    title="Regions"
                    options={_.get(props.settings, 'contentRegions', [])}
                    selected={_.get(props.userInfo, 'searchOptions.regions', [])}
                    onChange={values => onChangeDefaultFilter('regions', values)}
                  />
                }
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card bg="light">
              <Card.Header>Notification Settings</Card.Header>
              <Card.Body>
                <div className="flex justify-between items-center py-4">
                  <div>Web Notification</div>
                  <ToggleButton
                    value={_.get(props.userInfo, USER_SETTINGS.APP_OPTIONS.PUSH_ENABLED, true)}
                    thumbStyle={{ borderRadius: 2 }}
                    trackStyle={{ borderRadius: 2 }}
                    colors={{
                      active: {
                        base: 'rgb(76,210,126)',
                      } 
                    }}
                    onToggle={value => onToggleNotificationSettings(USER_SETTINGS.APP_OPTIONS.PUSH_ENABLED, value)}
                  />
                </div>
                <div className="flex justify-between items-center py-2">
                  <div>Extension Notification</div>
                  <ToggleButton
                    value={_.get(props.userInfo, USER_SETTINGS.EXTENSION_OPTIONS.PUSH_ENABLED, true)}
                    thumbStyle={{ borderRadius: 2 }}
                    trackStyle={{ borderRadius: 2 }}
                    colors={{
                      active: {
                        base: 'rgb(76,210,126)',
                      } 
                    }}
                    onToggle={value => onToggleNotificationSettings(USER_SETTINGS.EXTENSION_OPTIONS.PUSH_ENABLED, value)}
                  />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      {/* Uncomment if you need the recent activity section */}
      {/* <div className="recent-history-wrapper">
        <h3>Recent activity</h3>
        <HistoryCard activities={props.activities} />
      </div> */}
    </div>
  );
}

const mapStateToProps = state => ({
  user: state.auth.tenant || {},
  Auth: state.auth.Auth,
  cognitoUser: state.auth.cognitoUser,
  activities: state.activity.activities,
  settings: state.settings,
  userInfo: state.settings.userInfo
});

const mapDispatchToProps = dispatch => 
  bindActionCreators({
    getCurrentCognitoUser,
    changeUserAttribute,
    getUserInfo
  }, dispatch);

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Settings));